<script>
import { mapGetters } from 'vuex';
import { useAutoShutdownTimes } from 'dashboard/composables/useAutoShutdownTimes';
import { useAlert } from 'dashboard/composables';
import { frontendURL } from 'dashboard/helper/URLHelper';
import SettingsSection from 'dashboard/components/SettingsSection.vue';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { useAdmin } from 'dashboard/composables/useAdmin';
import TeimosinhaOptions from '../teimosinha/Options.vue';

export default {
  components: {
    LoadingState,
    SettingsSection,
    TeimosinhaOptions,
    NextButton,
  },
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  setup() {
    const { isAdmin } = useAdmin();
    const { autoShutdownTimes } = useAutoShutdownTimes();
    return { isAdmin, autoShutdownTimes };
  },
  data() {
    return {
      selectedAgentBotId: null,
      selectedAutoShutdownTime: 3600, // default time is 1 hour
      automaticShutdownIsEnabled: false,
      closingMessageContent: this.$t(
        'AGENT_BOTS.BOT_CONFIGURATION.AUTOMATIC_SHUTDOWN.SHUTDOWN_DEFAULT_MESSAGE'
      ),
      partialLeadIsEnabled: false,
      partialLeadFetchCsmlVariables: false,
      partialLeadTargetUrl: '',
      partialLeadAdditionalInfos: '',
      updateAutomaticActionsIsLoading: false,
      selectedLabels: [],
    };
  },
  computed: {
    ...mapGetters({
      agentBots: 'agentBots/getBots',
      uiFlags: 'agentBots/getUIFlags',
      labels: 'labels/getLabels',
    }),
    currentInboxId() {
      return this.inbox?.id || this.$route.params.inboxId;
    },
    activeAgentBot() {
      return this.$store.getters['agentBots/getActiveAgentBot'](
        this.currentInboxId
      );
    },
    activeAgentBotEditUrl() {
      if (!this.activeAgentBot?.id) return null;
      const { bot_type: botType, id } = this.activeAgentBot;
      const accountId = this.$store.getters.getCurrentAccountId;
      if (!['csml', 'n8n', 'typebot'].includes(botType)) return null;
      return frontendURL(
        `accounts/${accountId}/settings/agent-bots/${botType}/${id}`
      );
    },
  },
  watch: {
    activeAgentBot() {
      this.selectedAgentBotId = this.activeAgentBot.id;
    },
  },
  mounted() {
    this.$store.dispatch('agentBots/get');
    this.$store.dispatch('agentBots/fetchAgentBotInbox', this.inbox.id);
    this.setInitialCloseBotConfigValues();
    this.setInitialPartialLeadConfigValues();
  },

  methods: {
    openEditBotUrl() {
      window.open(this.activeAgentBotEditUrl, '_blank');
    },
    setInitialCloseBotConfigValues() {
      const close_bot_config =
        this.inbox.automatic_actions_settings?.close_bot_conversation;
      if (!close_bot_config?.time_to_close) return;
      this.automaticShutdownIsEnabled = true;

      const time_to_close = close_bot_config.time_to_close;
      if (time_to_close) this.selectedAutoShutdownTime = time_to_close;

      const message_content = close_bot_config.message_content;
      if (message_content) this.closingMessageContent = message_content;

      const labels_to_add = close_bot_config.labels_to_add;
      if (labels_to_add)
        this.selectedLabels = this.labels.filter(label =>
          labels_to_add.includes(label.id)
        );
    },
    setInitialPartialLeadConfigValues() {
      const partial_lead_config =
        this.inbox.automatic_actions_settings?.partial_lead;
      if (!partial_lead_config?.target_url) return;
      this.partialLeadIsEnabled = true;

      const fetch_csml_variables = partial_lead_config.fetch_csml_variables;
      if (fetch_csml_variables)
        this.partialLeadFetchCsmlVariables = fetch_csml_variables;

      const target_url = partial_lead_config.target_url;
      if (target_url) this.partialLeadTargetUrl = target_url;

      const additional_infos = partial_lead_config.additional_infos;
      if (additional_infos) this.partialLeadAdditionalInfos = additional_infos;
    },
    async updateActiveAgentBot() {
      try {
        await this.$store.dispatch('agentBots/setAgentBotInbox', {
          inboxId: this.inbox.id,
          // Added this to make sure that empty values are not sent to the API
          botId: this.selectedAgentBotId ? this.selectedAgentBotId : undefined,
        });
        useAlert(this.$t('AGENT_BOTS.BOT_CONFIGURATION.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('AGENT_BOTS.BOT_CONFIGURATION.ERROR_MESSAGE'));
      }
    },
    async disconnectBot() {
      try {
        await this.$store.dispatch('agentBots/disconnectBot', {
          inboxId: this.inbox.id,
        });
        useAlert(
          this.$t('AGENT_BOTS.BOT_CONFIGURATION.DISCONNECTED_SUCCESS_MESSAGE')
        );
      } catch (error) {
        useAlert(
          error?.message ||
            this.$t('AGENT_BOTS.BOT_CONFIGURATION.DISCONNECTED_ERROR_MESSAGE')
        );
      }
    },
    currentTimeTranslated(time) {
      const currentLabel = `AGENT_BOTS.BOT_CONFIGURATION.AUTOMATIC_SHUTDOWN.TIMES.${time.label}`;
      return this.$t(currentLabel);
    },
    async updateAutoShutdownTime() {
      return useAlert('ATT auto close');
    },
    preparedAutomaticActionsSettings() {
      const close_bot_conversation = this.automaticShutdownIsEnabled
        ? {
            time_to_close: this.selectedAutoShutdownTime,
            message_content: this.closingMessageContent,
            labels_to_add: this.selectedLabels.map(label => label.id),
          }
        : {};
      const partial_lead = this.partialLeadIsEnabled
        ? {
            target_url: this.partialLeadTargetUrl,
            additional_infos: this.partialLeadAdditionalInfos,
            fetch_csml_variables: this.partialLeadFetchCsmlVariables,
          }
        : {};
      return { close_bot_conversation, partial_lead };
    },
    async updateAutomaticActions() {
      const payload = {
        id: this.inbox.id,
        automatic_actions_settings: this.preparedAutomaticActionsSettings(),
      };
      try {
        this.updateAutomaticActionsIsLoading = true;
        await this.$store.dispatch(
          'inboxes/updateInboxAutomaticActionsSettings',
          payload
        );
        this.updateAutomaticActionsIsLoading = false;

        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
        this.updateAutomaticActionsIsLoading = false;
      }
    },
  },
};
</script>

<template>
  <div class="mx-6 max-w-4xl">
    <LoadingState v-if="uiFlags.isFetching || uiFlags.isFetchingAgentBot" />
    <div v-else>
      <form class="row" @submit.prevent="updateActiveAgentBot">
        <SettingsSection
          :title="$t('AGENT_BOTS.BOT_CONFIGURATION.TITLE')"
          :sub-title="$t('AGENT_BOTS.BOT_CONFIGURATION.DESC')"
        >
          <div class="medium-7 columns">
            <label>
              <select v-model="selectedAgentBotId">
                <option value="" disabled selected>
                  {{ $t('AGENT_BOTS.BOT_CONFIGURATION.SELECT_PLACEHOLDER') }}
                </option>
                <option
                  v-for="agentBot in agentBots"
                  :key="agentBot.id"
                  :value="agentBot.id"
                >
                  {{ agentBot.name }}
                </option>
              </select>
            </label>
            <div class="flex gap-2">
              <NextButton
                type="submit"
                :label="$t('AGENT_BOTS.BOT_CONFIGURATION.SUBMIT')"
                :is-loading="uiFlags.isSettingAgentBot"
              />
              <NextButton
                v-if="activeAgentBotEditUrl"
                type="button"
                icon="i-lucide-pen"
                variant="smooth"
                color-scheme="secondary"
                :label="$t('AGENT_BOTS.BOT_CONFIGURATION.EDIT_BOT')"
                @click="openEditBotUrl"
              />
              <NextButton
                type="button"
                :disabled="!selectedAgentBotId"
                :loading="uiFlags.isDisconnecting"
                variant="smooth"
                color-scheme="alert"
                class="button--disconnect"
                @click="disconnectBot"
              >
                {{ $t('AGENT_BOTS.BOT_CONFIGURATION.DISCONNECT') }}
              </NextButton>
            </div>
          </div>
        </SettingsSection>
      </form>
      <form @submit.prevent="updateAutomaticActions">
        <SettingsSection
          :title="$t('AGENT_BOTS.BOT_CONFIGURATION.AUTOMATIC_SHUTDOWN.TITLE')"
          :sub-title="
            $t('AGENT_BOTS.BOT_CONFIGURATION.AUTOMATIC_SHUTDOWN.DESC')
          "
        >
          <div class="medium-7 columns">
            <label>
              {{ $t('AGENT_BOTS.BOT_CONFIGURATION.AUTOMATIC_SHUTDOWN.HEADER') }}
              <select v-model="automaticShutdownIsEnabled">
                <option :value="true">
                  {{
                    $t('INBOX_MGMT.EDIT.ALLOW_MESSAGES_AFTER_RESOLVED.ENABLED')
                  }}
                </option>
                <option :value="false">
                  {{
                    $t('INBOX_MGMT.EDIT.ALLOW_MESSAGES_AFTER_RESOLVED.DISABLED')
                  }}
                </option>
              </select>
            </label>

            <label v-if="automaticShutdownIsEnabled">
              {{
                $t(
                  'AGENT_BOTS.BOT_CONFIGURATION.AUTOMATIC_SHUTDOWN.TIMES_HEADER'
                )
              }}
              <select v-model="selectedAutoShutdownTime">
                <option value="" disabled selected>
                  {{
                    $t(
                      'AGENT_BOTS.BOT_CONFIGURATION.AUTOMATIC_SHUTDOWN.SELECT_PLACEHOLDER'
                    )
                  }}
                </option>
                <option
                  v-for="time in autoShutdownTimes"
                  :key="time.interval"
                  :value="time.interval"
                >
                  {{ currentTimeTranslated(time) }}
                </option>
              </select>
            </label>

            <woot-input
              v-if="automaticShutdownIsEnabled"
              v-model.trim="closingMessageContent"
              v-tooltip.bottom="closingMessageContent"
              :label="
                $t(
                  'AGENT_BOTS.BOT_CONFIGURATION.AUTOMATIC_SHUTDOWN.SHUTDOWN_DEFAULT_MESSAGE_HEADER'
                )
              "
              :placeholder="
                $t(
                  'AGENT_BOTS.BOT_CONFIGURATION.AUTOMATIC_SHUTDOWN.SHUTDOWN_DEFAULT_MESSAGE_PLACEHOLDER'
                )
              "
            />

            <div v-if="automaticShutdownIsEnabled" class="mb-4">
              <label>{{
                $t(
                  'AGENT_BOTS.BOT_CONFIGURATION.AUTOMATIC_SHUTDOWN.ADD_LABELS_HEADER'
                )
              }}</label>
              <div class="multiselect-wrap--small">
                <multiselect
                  v-model="selectedLabels"
                  class="no-margin text-xxs"
                  track-by="id"
                  label="title"
                  selected-label
                  multiple
                  :close-on-select="false"
                  :clear-on-select="false"
                  hide-selected
                  :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
                  :placeholder="
                    $t(
                      'AGENT_BOTS.BOT_CONFIGURATION.AUTOMATIC_SHUTDOWN.ADD_LABELS_PLACEHOLDER'
                    )
                  "
                  deselect-label=""
                  :options="labels"
                  searchable
                  allow-empty
                  :max-height="150"
                />
              </div>
            </div>

            <div class="button-container">
              <NextButton
                type="submit"
                :label="$t('AGENT_BOTS.BOT_CONFIGURATION.SUBMIT')"
                :is-loading="updateAutomaticActionsIsLoading"
              />
            </div>
          </div>
        </SettingsSection>
      </form>

      <form v-if="isAdmin" @submit.prevent="updateAutomaticActions">
        <SettingsSection
          :title="$t('AGENT_BOTS.BOT_CONFIGURATION.PARTIAL_LEADS.TITLE')"
          :sub-title="$t('AGENT_BOTS.BOT_CONFIGURATION.PARTIAL_LEADS.DESC')"
        >
          <div class="medium-7 columns">
            <label>
              {{ $t('AGENT_BOTS.BOT_CONFIGURATION.PARTIAL_LEADS.HEADER') }}
              <select v-model="partialLeadIsEnabled">
                <option :value="true">
                  {{
                    $t('INBOX_MGMT.EDIT.ALLOW_MESSAGES_AFTER_RESOLVED.ENABLED')
                  }}
                </option>
                <option :value="false">
                  {{
                    $t('INBOX_MGMT.EDIT.ALLOW_MESSAGES_AFTER_RESOLVED.DISABLED')
                  }}
                </option>
              </select>
            </label>

            <label v-if="partialLeadIsEnabled">
              {{
                $t(
                  'AGENT_BOTS.BOT_CONFIGURATION.PARTIAL_LEADS.FETCH_VARS_HEADER'
                )
              }}
              <select v-model="partialLeadFetchCsmlVariables">
                <option :value="true">
                  {{
                    $t('INBOX_MGMT.EDIT.ALLOW_MESSAGES_AFTER_RESOLVED.ENABLED')
                  }}
                </option>
                <option :value="false">
                  {{
                    $t('INBOX_MGMT.EDIT.ALLOW_MESSAGES_AFTER_RESOLVED.DISABLED')
                  }}
                </option>
              </select>
            </label>

            <woot-input
              v-if="partialLeadIsEnabled"
              v-model.trim="partialLeadTargetUrl"
              v-tooltip.bottom="
                $t(
                  'AGENT_BOTS.BOT_CONFIGURATION.PARTIAL_LEADS.TARGET_URL_TOOLTIP'
                )
              "
              :label="
                $t('AGENT_BOTS.BOT_CONFIGURATION.PARTIAL_LEADS.TARGET_URL')
              "
              :placeholder="
                $t(
                  'AGENT_BOTS.BOT_CONFIGURATION.PARTIAL_LEADS.TARGET_URL_PLACEHOLDER'
                )
              "
            />
            <woot-input
              v-if="partialLeadIsEnabled"
              v-model.trim="partialLeadAdditionalInfos"
              :label="
                $t(
                  'AGENT_BOTS.BOT_CONFIGURATION.PARTIAL_LEADS.ADDITIONAL_INFOS'
                )
              "
              :placeholder="
                $t(
                  'AGENT_BOTS.BOT_CONFIGURATION.PARTIAL_LEADS.ADDITIONAL_INFOS_PLACEHOLDER'
                )
              "
            />
            <div class="button-container">
              <NextButton
                type="submit"
                :label="$t('AGENT_BOTS.BOT_CONFIGURATION.SUBMIT')"
                :disabled="partialLeadIsEnabled && !partialLeadTargetUrl"
                :is-loading="updateAutomaticActionsIsLoading"
              />
            </div>
          </div>
        </SettingsSection>
      </form>
      <br />
      <SettingsSection
        :title="$t('INBOX_MGMT.TEIMOSINHA.TITLE')"
        :sub-title="$t('INBOX_MGMT.TEIMOSINHA.SUBTITLE')"
        :show-border="false"
      >
        <TeimosinhaOptions :inbox="inbox" />
      </SettingsSection>
    </div>
  </div>
</template>
